import React, { useState } from "react";
import {
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Link
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";

const FAQSearch = ({ questions, activeTab, setActiveTab }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  // Fonction pour détecter et convertir les URLs en liens cliquables
  const renderTextWithLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <Link
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: "#1976d2", textDecoration: "underline", fontWeight: 500 }}
          >
            {t("getHelp.linkText", "this link")}
          </Link>
        );
      }
      return part;
    });
  };

  // Utilise les questions passées en prop, sinon fallback sur t('faq.questions')
  let data = questions;
  if (!data) {
    const i18nQuestions = t("getHelp.questionsSearch", { returnObjects: true });
    if (Array.isArray(i18nQuestions)) {
      data = i18nQuestions;
    } else {
      data = [];
    }
  }

  // Filtrage par catégorie (onglet actif)
  const filteredByCategory = data.filter((q) => q.category === activeTab);

  // Filtrage insensible à la casse sur question + réponse
  const filtered = filteredByCategory.filter(
    (q) =>
      q.question.toLowerCase().includes(search.toLowerCase()) ||
      q.answer.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* Bloc principal unifié */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: `${activeTab === "ETA-IL" ? "0px 8px 8px 8px" : "8px 8px 8px 8px"}`,
          boxShadow: "0 2px 16px rgba(24,59,86,0.07)",
          px: { xs: 2, sm: 2, md: 6 },
          py: { xs: 2, sm: 3, md: 3 },
        }}
      >
        {/* Section recherche + intro + image */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            gap: 2,
            mb: 4,
          }}
        >
          <Box sx={{ flex: 2 }}>
            <Box sx={{ mb: 2, mt: 2 }}>
              <Typography
                variant="h6"
                component="h2"
                aria-level="2"
                sx={{ fontSize: 16, fontWeight: 400, color: "#183B56" }}
              >
                {t("getHelp.text")}
              </Typography>
              <Typography
                variant="h6"
                component="h2"
                aria-level="2"
                sx={{ fontSize: 16, fontWeight: 400, color: "#183B56" }}
              >
                {t("getHelp.text2")}
              </Typography>
            </Box>
            {/* Image entre le texte et le champ sur mobile */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                justifyContent: "center",
                alignItems: "center",
                mb: 2,
              }}
            >
                <Box component="img" src={`${process.env.PUBLIC_URL}/getHelp.png`}  sx={{ maxWidth: 180, width: "100%" }} />
            </Box>
            <Typography
              variant="h6"
              component="h3"
              aria-level="3"
              sx={{ fontSize: 24, fontWeight: 600, color: "#183B56", mb: 2, mt: 2 }}
            >
              {t("getHelp.label")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2, mb: 3 }}>
              <TextField
                label={t("getHelp.label")}
                placeholder={t("getHelp.placeholder")}
                variant="outlined"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  width: { xs: "100%", md: "70%" },
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  boxShadow: "0 2px 16px rgba(24,59,86,0.07)",
                }}
              />
            </Box>
          </Box>
          {/* Image à droite sur desktop */}
          <Box
            sx={{
              flex: 0.5,
              display: { xs: "none", md: "flex" },
              justifyContent: "right",
              alignItems: "center",
            }}
          >
            <Box component="img" src={`${process.env.PUBLIC_URL}/getHelp.png`} alt="" />
          </Box>
        </Box>

        {/* FAQ filtrée */}
        {filtered.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            {t("getHelp.noResult")}
          </Typography>
        ) : (
          filtered.map((q, idx) => (
            <Accordion key={idx} sx={{ mb: 1 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  "&.Mui-focusVisible, &:focus-visible": {
                    outline: '3px solid #000',
                    outlineOffset: '2px',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>{q.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ width: "100%", wordBreak: "break-word", overflowWrap: "break-word" }}>{renderTextWithLinks(q.answer)}</Typography>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
    </>
  );
};

export default FAQSearch;